import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Toolbar,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MergeIcon from '@mui/icons-material/Merge';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

interface Hashtag {
  id: number;
  name: string;
  category: string | null;
  postCount: number;
  actualPostCount: number;
  createdAt: string;
}

interface ListResponse {
  items: Hashtag[];
  meta: { total: number; page: number; limit: number; totalPages: number };
  categories: string[];
}

const HashtagList: React.FC = () => {
  const [tags, setTags] = useState<Hashtag[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'postCount' | 'name' | 'createdAt'>('postCount');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // 선택 (병합용)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // 다이얼로그 상태
  const [editTarget, setEditTarget] = useState<Hashtag | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Hashtag | null>(null);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortOrder,
      };
      if (search) params.search = search;
      if (categoryFilter !== '') params.category = categoryFilter;

      const r = await axios.get<ListResponse>(`${API_BASE_URL}/admin/hashtags`, {
        headers: authHeader(),
        params,
      });
      setTags(r.data.items);
      setTotal(r.data.meta.total);
      setCategories(r.data.categories);
    } catch (err: any) {
      setError(err.response?.data?.message || '태그를 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, search, categoryFilter, sortBy, sortOrder, authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  return (
    <Box>
      <Toolbar disableGutters sx={{ mb: 2, gap: 2 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          태그 관리
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={isLoading}
        >
          새로고침
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          태그 생성
        </Button>
      </Toolbar>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="이름 검색"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>카테고리</InputLabel>
            <Select
              value={categoryFilter}
              label="카테고리"
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="null">(미지정)</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>정렬 기준</InputLabel>
            <Select
              value={sortBy}
              label="정렬 기준"
              onChange={(e) => setSortBy(e.target.value as any)}
            >
              <MenuItem value="postCount">사용 빈도</MenuItem>
              <MenuItem value="name">이름</MenuItem>
              <MenuItem value="createdAt">생성일</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>방향</InputLabel>
            <Select
              value={sortOrder}
              label="방향"
              onChange={(e) => setSortOrder(e.target.value as any)}
            >
              <MenuItem value="desc">내림차순</MenuItem>
              <MenuItem value="asc">오름차순</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {selectedIds.size >= 2 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body2">
              선택된 태그 {selectedIds.size}개 — 하나로 병합할 수 있습니다.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<MergeIcon />}
              onClick={() => setMergeOpen(true)}
            >
              병합
            </Button>
            <Button size="small" onClick={clearSelection}>
              선택 해제
            </Button>
          </Stack>
        </Paper>
      )}

      <Paper>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>이름</TableCell>
                    <TableCell>카테고리</TableCell>
                    <TableCell align="right">사용 수</TableCell>
                    <TableCell>생성일</TableCell>
                    <TableCell align="right">액션</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tags.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          검색 결과가 없습니다.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tags.map((tag) => (
                      <TableRow key={tag.id} hover selected={selectedIds.has(tag.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(tag.id)}
                            onChange={() => toggleSelect(tag.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">#{tag.name}</Typography>
                        </TableCell>
                        <TableCell>
                          {tag.category ? (
                            <Chip label={tag.category} size="small" color="primary" variant="outlined" />
                          ) : (
                            <Typography variant="caption" color="text.secondary">미지정</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {tag.actualPostCount}
                            {tag.postCount !== tag.actualPostCount && (
                              <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                                (캐시: {tag.postCount})
                              </Typography>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(tag.createdAt).toLocaleDateString('ko-KR')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => setEditTarget(tag)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => setDeleteTarget(tag)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[15, 30, 50, 100]}
              labelRowsPerPage="페이지당 항목"
            />
          </>
        )}
      </Paper>

      {/* 생성 다이얼로그 */}
      {createOpen && (
        <HashtagEditDialog
          mode="create"
          existingCategories={categories}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            load();
          }}
          authHeader={authHeader}
        />
      )}

      {/* 편집 다이얼로그 */}
      {editTarget && (
        <HashtagEditDialog
          mode="edit"
          hashtag={editTarget}
          existingCategories={categories}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            load();
          }}
          authHeader={authHeader}
        />
      )}

      {/* 삭제 확인 */}
      {deleteTarget && (
        <Dialog open onClose={() => setDeleteTarget(null)}>
          <DialogTitle>태그 삭제</DialogTitle>
          <DialogContent>
            <Typography>
              태그 <strong>#{deleteTarget.name}</strong> 을(를) 삭제하시겠습니까?
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              연결된 게시물 {deleteTarget.actualPostCount}건과의 관계가 모두 해제됩니다.
              게시물 자체는 유지됩니다.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>취소</Button>
            <Button
              color="error"
              variant="contained"
              onClick={async () => {
                try {
                  await axios.delete(`${API_BASE_URL}/admin/hashtags/${deleteTarget.id}`, {
                    headers: authHeader(),
                  });
                  setDeleteTarget(null);
                  load();
                } catch (err: any) {
                  setError(err.response?.data?.message || '삭제 실패');
                }
              }}
            >
              삭제
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* 병합 다이얼로그 */}
      {mergeOpen && (
        <MergeDialog
          selectedTags={tags.filter((t) => selectedIds.has(t.id))}
          onClose={() => setMergeOpen(false)}
          onSaved={() => {
            setMergeOpen(false);
            clearSelection();
            load();
          }}
          authHeader={authHeader}
        />
      )}
    </Box>
  );
};

// 태그 생성·편집 공용 다이얼로그
const HashtagEditDialog: React.FC<{
  mode: 'create' | 'edit';
  hashtag?: Hashtag;
  existingCategories: string[];
  onClose: () => void;
  onSaved: () => void;
  authHeader: () => { Authorization: string };
}> = ({ mode, hashtag, existingCategories, onClose, onSaved, authHeader }) => {
  const [name, setName] = useState(hashtag?.name || '');
  const [category, setCategory] = useState(hashtag?.category || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    try {
      const body = { name: name.trim(), category: category.trim() || undefined };
      if (mode === 'create') {
        await axios.post(`${API_BASE_URL}/admin/hashtags`, body, { headers: authHeader() });
      } else if (hashtag) {
        await axios.patch(`${API_BASE_URL}/admin/hashtags/${hashtag.id}`, body, { headers: authHeader() });
      }
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || '저장 실패');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === 'create' ? '태그 생성' : '태그 편집'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="태그 이름"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            helperText="1~50자, 영문/한글/숫자/_/-"
          />
          <FormControl fullWidth>
            <InputLabel>카테고리</InputLabel>
            <Select
              value={category}
              label="카테고리"
              onChange={(e) => setCategory(e.target.value)}
            >
              <MenuItem value="">(미지정)</MenuItem>
              {existingCategories.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="새 카테고리 (직접 입력)"
            value={existingCategories.includes(category) ? '' : category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
            helperText="위 목록에 없으면 여기 입력. 예: 어종, 장소, 장비, 계절"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>취소</Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving || !name.trim()}>
          {isSaving ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// 병합 다이얼로그
const MergeDialog: React.FC<{
  selectedTags: Hashtag[];
  onClose: () => void;
  onSaved: () => void;
  authHeader: () => { Authorization: string };
}> = ({ selectedTags, onClose, onSaved, authHeader }) => {
  const [targetId, setTargetId] = useState<number | ''>('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const handleMerge = async () => {
    if (targetId === '') return;
    setIsSaving(true);
    setError('');
    try {
      const sourceIds = selectedTags
        .map((t) => t.id)
        .filter((id) => id !== targetId);
      const r = await axios.post(
        `${API_BASE_URL}/admin/hashtags/merge`,
        { sourceIds, targetId },
        { headers: authHeader() },
      );
      alert(r.data.message || '병합 완료');
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || '병합 실패');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>태그 병합</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" sx={{ mb: 2 }}>
          선택된 {selectedTags.length}개 태그 중 하나를 <strong>대상 태그</strong>로 지정하세요.
          나머지 태그의 모든 게시물 연결이 대상으로 이관되고, 나머지 태그는 삭제됩니다.
        </Typography>
        <FormControl fullWidth>
          <InputLabel>대상 태그 (남길 것)</InputLabel>
          <Select
            value={targetId}
            label="대상 태그 (남길 것)"
            onChange={(e) => setTargetId(e.target.value as number)}
          >
            {selectedTags.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                #{t.name} {t.category ? `(${t.category})` : ''} · 사용 {t.actualPostCount}건
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>취소</Button>
        <Button
          color="warning"
          variant="contained"
          onClick={handleMerge}
          disabled={isSaving || targetId === ''}
        >
          {isSaving ? '병합 중...' : '병합 실행'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HashtagList;
